// ─── Types ────────────────────────────────────────────────────────────────────

interface PantryItemForNotif {
  id:                      string
  name:                    string
  current_expiration_date: string | null
}

// ─── Permission ───────────────────────────────────────────────────────────────

export async function requestNotificationPermission(): Promise<boolean> {
  if (typeof window === 'undefined' || !('Notification' in window)) return false
  if (Notification.permission === 'granted') return true
  if (Notification.permission === 'denied')  return false
  const result = await Notification.requestPermission()
  return result === 'granted'
}

// ─── safeNotify ──────────────────────────────────────────────────────────────
//
// Mobile Chrome forbids `new Notification(...)` — it throws "Illegal constructor".
// Mobile browsers require the service-worker path via ServiceWorkerRegistration
// .showNotification(). Desktop Chrome accepts both, so we prefer the SW path
// everywhere and only fall back to the direct constructor when no SW is
// registered. Every failure path logs a warning and returns cleanly — a
// notification failure MUST NOT bubble up and crash the app.

export async function safeNotify(
  title:   string,
  options: NotificationOptions = {},
): Promise<void> {
  if (typeof window === 'undefined' || !('Notification' in window)) return
  if (Notification.permission !== 'granted') return

  // Prefer the service-worker path (works on mobile + desktop)
  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready
      await reg.showNotification(title, options)
      return
    } catch (err) {
      console.warn('SW showNotification failed, falling back:', err)
    }
  }

  // Desktop-only fallback. Wrap in try/catch because mobile will throw here.
  try {
    new Notification(title, options)
  } catch (err) {
    console.warn('Notification not supported in this context:', err)
  }
}

// ─── Check expiring items and fire browser notifications ─────────────────────

export async function checkExpiringItems(items: PantryItemForNotif[]): Promise<void> {
  if (typeof window === 'undefined' || !('Notification' in window)) return
  if (Notification.permission !== 'granted') return

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  for (const item of items) {
    if (!item.current_expiration_date) continue

    const expDate = new Date(item.current_expiration_date + 'T00:00:00')
    expDate.setHours(0, 0, 0, 0)
    const daysUntil = Math.round(
      (expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    )

    if (daysUntil === 2) {
      await safeNotify('Pantry — Expiring soon', {
        body: `Your ${item.name} expires in 2 days. Use it before it goes bad.`,
        icon: '/pwa-192.png',
        tag:  `expiry-2day-${item.id}`,
      })
    }

    if (daysUntil === 0) {
      await safeNotify('Pantry — Expires today', {
        body: `Your ${item.name} expires today. Use it now or mark it as wasted.`,
        icon: '/pwa-192.png',
        tag:  `expiry-today-${item.id}`,
      })
    }
  }
}
